/**
 * Die Vorschauzeile eines gespiegelten Tages.
 *
 * Martin, 26.08.: *„Das schaut einfach nur scheiße aus."*
 *
 * Im Verlauf stand unter jedem Datum derselbe Anfang:
 *
 *   Di., 25. August 2026 (22:19)
 *   Spiegel des Tages — 25. August 2026 Dein Tag Ein dünner Tag, was Worte…
 *
 * Der Grund: die Vorschau nahm den Markdown-Text roh, entfernte nur die
 * Sonderzeichen und schnitt 150 Zeichen ab. Die Überschrift („Spiegel des
 * Tages — 25. August 2026") und die erste Zwischenüberschrift („Dein Tag")
 * standen also mitten drin — und das Datum ein zweites Mal, direkt unter dem
 * Datum.
 *
 * Von 150 Zeichen Vorschau waren rund 40 immer dieselben.
 *
 * ⚠️ 10.09.2026 — diese Datei lag unter `frontend/lib/`. Seit der Server die
 *    Verlaufsliste selbst kürzt (der volle Text wog 353 kB für eine Liste,
 *    die 150 Zeichen je Zeile zeigt), lesen BEIDE Seiten dieselbe Funktion.
 *    Eine zweite Kürzung im Server wäre die nächste Stelle, die auseinander
 *    läuft — genau wie bei der Abschnitts-Zerlegung (shared/vorlese-abschnitte).
 */

/** Länge der Vorschau. Zwei Zeilen auf dem Telefon. */
const LAENGE = 150;

/**
 * Der erste echte Satz eines Spiegels, ohne Überschriften.
 *
 * @param markdown Der volle Text der Spiegelung.
 * @param datum    Das Datum, das die Zeile darüber schon zeigt — damit eine
 *                 Überschrift, die es wiederholt, nicht doch durchrutscht.
 */
export function spiegelVorschau(markdown: string, datum = ""): string {
  const zeilen = (markdown || "").split(/\r?\n/);
  const prosa: string[] = [];

  for (const roh of zeilen) {
    const z = roh.trim();
    if (!z) continue;

    // Markdown-Überschriften (# … ######) sind nie der Anfang eines Gedankens.
    if (/^#{1,6}\s/.test(z)) continue;

    // Fettgesetzte Alleinzeilen sind ebenfalls Überschriften ("**Dein Tag**").
    if (/^\*{2}[^*]+\*{2}$/.test(z)) continue;

    // Trennlinien, Listenzeichen ohne Inhalt.
    if (/^[-–—_*=]{3,}$/.test(z)) continue;

    const sauber = putze(z);
    if (!sauber) continue;

    // Eine Zeile, die nur den Titel oder das Datum wiederholt, trägt nichts.
    if (prosa.length === 0 && istUeberschrift(sauber, datum)) continue;

    prosa.push(sauber);
    // Zwei Zeilen reichen, um auf Länge zu kommen.
    if (prosa.join(" ").length >= LAENGE) break;
  }

  const text = prosa.join(" ").trim();
  if (!text) return "";
  return text.length > LAENGE ? `${text.slice(0, LAENGE).trimEnd()}…` : text;
}

/** Markdown-Auszeichnung entfernen, Leerraum vereinheitlichen. */
function putze(s: string): string {
  return s
    .replace(/`{1,3}[^`]*`{1,3}/g, " ")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#*_>`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Ist diese Zeile bloss eine Überschrift?
 *
 * Zwei Merkmale, beide notwendig — eine kurze Zeile allein reicht nicht, sonst
 * verschluckt die Vorschau einen echten kurzen Satz:
 *   · sie ist kurz (bis 60 Zeichen)
 *   · sie endet ohne Satzzeichen ODER wiederholt das Datum darüber
 */
function istUeberschrift(zeile: string, datum: string): boolean {
  if (zeile.length > 60) return false;
  const ohneSatzende = !/[.!?…]$/.test(zeile);
  const wiederholtDatum =
    datum.length > 3 &&
    zeile.toLowerCase().includes(datum.toLowerCase().slice(0, 12));
  return ohneSatzende || wiederholtDatum;
}
